// Docs: IMPLEMENTATION.md §2.7.4 "Time handling". Update it when the rules below change.
//
// The single place that knows how to turn a backend time value into something
// a human reads. Two kinds of value cross the API boundary and they must NOT
// be treated alike:
//
//  1. **Instants** — a moment on the clock: `created_at`, `next_due`,
//     `started_at`, `answered_at`, `validated_at`, `start_utc`, …
//     Stored in UTC, serialized with an explicit `Z`, and rendered in the
//     *browser's* timezone. Use `parseInstant` / `formatInstant*`.
//
//  2. **User-day keys** — `YYYY-MM-DD` strings: `date`, `today_local`,
//     `week_start`, `period_start`, the keys of `schedule_by_date`, …
//     These are *already* calendar labels the backend computed in the user's
//     configured timezone using the 03:00 day cutoff (see backend
//     `day_window.py`). They are not instants and carry no time of day, so
//     converting them to local time is always wrong: `new Date('2026-09-21')`
//     is midnight **UTC**, which a viewer in New York renders as
//     "20. September". Use `parseDayKey` / `formatDayLabel`, which pin the
//     formatter to UTC so the label matches the key it came from.
//
// Rule of thumb: if the value has a `T` in it, it is an instant — render it
// local. If it is ten characters of `YYYY-MM-DD`, it is a label — render it
// verbatim. Never call `new Date(x)` on a backend value directly.

import { format as timeagoFormat } from 'timeago.js';

const DEFAULT_LOCALE = 'de-DE';

// ── Instants ────────────────────────────────────────────────────────────────

/**
 * Parse a backend instant into a `Date`.
 *
 * Tolerates the three shapes the API has historically produced — `…Z`,
 * `…+00:00`, and a bare `…T12:00:00` with no designator at all — by treating
 * a missing designator as UTC (which is what the DB columns hold). Never
 * appends `Z` to a string that already carries an offset; doing so yields
 * `Invalid Date`.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null} `null` for empty or unparseable input.
 */
export const parseInstant = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  try {
    let dateStr = String(value).trim();
    const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateStr);
    if (dateStr.includes('T') && !hasZone) {
      // Trim long fractional seconds to avoid browser parsing issues, then
      // mark the naive value as the UTC instant it actually is.
      dateStr = dateStr.replace(/\.\d{3,}$/, '') + 'Z';
    }
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch (e) {
    console.error('Error parsing backend instant:', e, value);
    return null;
  }
};

/** An instant as a local date+time string, or `fallback` if unparseable. */
export const formatInstant = (value, opts = {}, locale = DEFAULT_LOCALE, fallback = '') => {
  const d = parseInstant(value);
  if (!d) return fallback;
  return d.toLocaleString(locale, opts);
};

/** An instant as a local date string (no time of day). */
export const formatInstantDate = (value, opts = {}, locale = DEFAULT_LOCALE, fallback = '') => {
  const d = parseInstant(value);
  if (!d) return fallback;
  return d.toLocaleDateString(locale, opts);
};

/** An instant as a local time-of-day string. */
export const formatInstantTime = (value, opts = {}, locale = DEFAULT_LOCALE, fallback = '') => {
  const d = parseInstant(value);
  if (!d) return fallback;
  return d.toLocaleTimeString(locale, opts);
};

/** An instant as "vor 3 Stunden" (timeago), relative to now. */
export const formatInstantRelative = (value, locale = 'de', fallback = '') => {
  const d = parseInstant(value);
  if (!d) return fallback;
  return timeagoFormat(d, locale);
};

/** Milliseconds elapsed since an instant, or `null` if unparseable. */
export const msSince = (value, now = Date.now()) => {
  const d = parseInstant(value);
  return d ? now - d.getTime() : null;
};

// ── User-day keys ───────────────────────────────────────────────────────────

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A `YYYY-MM-DD` day key as a `Date` anchored at midnight **UTC**.
 *
 * UTC is the arithmetic frame for day keys: it has no DST, so stepping by
 * 24h never lands on the wrong date. Always render the result with
 * `formatDayLabel` (or an explicit `timeZone: 'UTC'`), never with the
 * browser's local getters.
 *
 * @returns {Date|null}
 */
export const parseDayKey = (key) => {
  if (key instanceof Date) return Number.isNaN(key.getTime()) ? null : key;
  const m = DAY_KEY_RE.exec(String(key || ''));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** A `Date` back to its `YYYY-MM-DD` key, read in UTC to match `parseDayKey`. */
export const formatDayKey = (date) => {
  const d = date instanceof Date ? date : parseDayKey(date);
  if (!d || Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** `key` shifted by `n` days (negative goes back), as a day key. */
export const shiftDayKey = (key, n) => {
  const d = parseDayKey(key);
  if (!d) return '';
  d.setUTCDate(d.getUTCDate() + n);
  return formatDayKey(d);
};

/**
 * A day key as a human label — pinned to UTC so the rendered date is the one
 * in the key, not the key shifted by the viewer's offset.
 */
export const formatDayLabel = (key, opts = {}, locale = DEFAULT_LOCALE, fallback = '') => {
  const d = parseDayKey(key);
  if (!d) return fallback;
  return d.toLocaleDateString(locale, { ...opts, timeZone: 'UTC' });
};

/**
 * The browser's own calendar day as a day key.
 *
 * Only a *fallback* for when `GET /user/day-window`'s `today_local` hasn't
 * loaded: it uses the browser's timezone and midnight (not the user's
 * configured timezone and the 03:00 cutoff), so it can disagree with the
 * backend around the rollover. Prefer `today_local` wherever it is available.
 */
export const browserDayKey = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
